import { useCallback, useDeferredValue, useState, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, ChevronRight, Loader2 } from "lucide-react";
import { orpc } from "@/frontend/orpc-client";
import { Input } from "@/frontend/components/ui/input";
import { cn } from "@/frontend/lib/utils";
import { logoUrl } from "@/frontend/utils/shared";
import { deletePatientDataAuthorizePath } from "@/server/constants/delete-patient-data";

/**
 * The signed-out half of `/delete-your-data`, where the patient finds a portal
 * they have signed in at before and starts an identity-only OAuth round trip.
 * The search runs off a deferred copy of the field value, so typing stays
 * responsive without a debounce timer or an effect. A clicked row spins until
 * the browser leaves for the portal.
 */
export function PortalSignIn() {
  const [query, setQuery] = useState("");
  const [openingUrl, setOpeningUrl] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query.trim());
  const enabled = deferredQuery.length >= 2;

  const sourcesQuery = useQuery({
    ...orpc.deletePatientData.searchSources.queryOptions({ input: { q: deferredQuery, limit: 20 } }),
    enabled,
  });

  const sources = sourcesQuery.data?.sources ?? [];
  const searching = enabled && sourcesQuery.isFetching;
  const sandbox = sourcesQuery.data?.sandbox ?? false;

  /**
   * Clears the spinner when the browser restores this page from its back-forward
   * cache, which happens when the patient presses back on the portal's login.
   *
   * @param node The picker's root element, or null on unmount.
   * @returns The cleanup that removes the listener.
   */
  const listenForReturn = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    /** Resets the opening row on a back-forward cache restore. */
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setOpeningUrl(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  /**
   * Marks a row as opening when its click navigates this tab. A modified click
   * opens a new tab instead, so this page has nothing to wait for.
   *
   * @param event The click on the row's link.
   * @param fhirBaseUrl The health system the row signs in at.
   */
  const onOpenSource = (event: MouseEvent<HTMLAnchorElement>, fhirBaseUrl: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    setOpeningUrl(fhirBaseUrl);
  };

  return (
    <div ref={listenForReturn} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <label htmlFor="portal-search" className="text-caption font-medium text-muted-foreground">
          Search for your hospital, clinic or insurer
        </label>
        {sandbox && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-meta font-medium text-muted-foreground">
            Sandbox
          </span>
        )}
      </div>

      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id="portal-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Mayo Clinic, Aetna, Medicare…"
          autoComplete="off"
          className="h-11 pl-9 text-body"
        />
      </div>

      <div className="min-h-24">
        {!enabled && (
          <p className="px-1 py-3 text-caption text-muted-foreground">
            Type at least two letters to search.
          </p>
        )}

        {enabled && searching && sources.length === 0 && (
          <p className="px-1 py-3 text-caption text-muted-foreground">Searching…</p>
        )}

        {enabled && !searching && sources.length === 0 && (
          <p className="px-1 py-3 text-caption text-muted-foreground">
            No match. Try the name printed on your bill or insurance card.
          </p>
        )}

        {sources.length > 0 && (
          <ul className="divide-y rounded-lg border">
            {sources.map((source) => {
              const opening = openingUrl === source.fhirBaseUrl;
              // Another row is on its way out, so this one must not start a second sign-in.
              const blocked = openingUrl !== null && !opening;
              return (
                <li key={source.fhirBaseUrl}>
                  <a
                    href={deletePatientDataAuthorizePath(source.fhirBaseUrl)}
                    onClick={(event) => onOpenSource(event, source.fhirBaseUrl)}
                    aria-busy={opening || undefined}
                    aria-disabled={blocked || undefined}
                    tabIndex={blocked ? -1 : undefined}
                    className={cn(
                      "flex min-h-11 w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                      opening && "bg-accent",
                      blocked && "pointer-events-none opacity-50",
                    )}
                  >
                    {logoUrl(source.logo) ? (
                      <img src={logoUrl(source.logo) ?? ""} alt="" className="size-7 shrink-0 rounded object-contain" />
                    ) : (
                      <span
                        aria-hidden
                        className="flex size-7 shrink-0 items-center justify-center rounded bg-muted text-meta font-medium text-muted-foreground"
                      >
                        {source.name.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 break-words text-body font-medium">{source.name}</span>
                    {opening ? (
                      <Loader2 aria-label="Opening portal" className="size-4 shrink-0 animate-spin text-muted-foreground" />
                    ) : (
                      <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
